// ClientAddressUpdatePage.jsx
// Public page reached via an emailed single-use link (/update-address?token=...).
// The client reviews and updates the address/contact info we have on file.
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const wrap = {
  minHeight: '100vh', background: 'var(--surface-muted)', padding: '24px',
  fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
};
const card = {
  background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
  width: '100%', maxWidth: 460, overflow: 'hidden', marginTop: 32,
};
const header = { background: 'var(--primary-color)', color: '#ffffff', padding: '22px 24px' };
const body = { padding: 24 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4, marginTop: 12 };
const input = {
  width: '100%', padding: '9px 10px', borderRadius: 6,
  border: '1px solid var(--border-color)', fontSize: 14, boxSizing: 'border-box',
};
const btn = {
  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
  padding: '11px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 15, width: '100%', marginTop: 18,
};

function getToken() {
  return new URLSearchParams(window.location.search).get('token') || '';
}

const REASON_MSG = {
  invalid: 'This link is not valid. Please contact the office for a new one.',
  expired: 'This link has expired. Please contact the office for a new one.',
  used: 'This link has already been used. If your information changes again, please ask the office for a new link.',
  error: 'Something went wrong checking this link. Please try again later.',
};

export default function ClientAddressUpdatePage() {
  const [token] = useState(getToken);
  const [status, setStatus] = useState('loading'); // loading | ready | invalid | done
  const [reason, setReason] = useState('');
  const [clientName, setClientName] = useState('');

  const [form, setForm] = useState({
    address: '', city: '', state: '', zipCode: '', phone: '', email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); setReason('invalid'); return; }
    axios.get(`/api/forms/address-update/validate?token=${encodeURIComponent(token)}`)
      .then(res => {
        if (res.data?.success) {
          setClientName(res.data.clientName || '');
          setForm((f) => ({ ...f, ...(res.data.current || {}) }));
          setStatus('ready');
        } else {
          setReason(res.data?.reason || 'invalid');
          setStatus('invalid');
        }
      })
      .catch(() => { setReason('error'); setStatus('invalid'); });
  }, [token]);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.address.trim()) {
      setError('Please enter your street address.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const res = await axios.post('/api/forms/address-update/submit', { token, info: form });
      if (res.data?.success) {
        setStatus('done');
      } else {
        setError(res.data?.message || 'Could not save your information. Please try again.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 23, fontWeight: 800, color: '#ffffff', letterSpacing: 0.2, textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>Update Your Contact Info</h2>
          <div style={{ fontSize: 13, color: '#ffffff', opacity: 0.95, marginTop: 5 }}>Avenues Unlimited Counseling Center</div>
        </div>
        <div style={body}>
          {status === 'loading' && <p style={{ color: '#64748b' }}>Checking your link…</p>}

          {status === 'invalid' && (
            <p style={{ color: '#b91c1c', fontSize: 15 }}>{REASON_MSG[reason] || REASON_MSG.invalid}</p>
          )}

          {status === 'done' && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <h3 style={{ margin: '8px 0' }}>Information updated</h3>
              <p style={{ color: 'var(--text-strong)' }}>
                Thank you{clientName ? `, ${clientName}` : ''}. Your contact information
                has been updated. You can close this page.
              </p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p style={{ color: 'var(--text-strong)', fontSize: 14, marginTop: 0 }}>
                {clientName ? `Hi ${clientName} — please` : 'Please'} review and update
                the information below, then save.
              </p>

              <label style={label}>Street Address</label>
              <input style={input} value={form.address} onChange={setField('address')} autoComplete="street-address" />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                <div>
                  <label style={label}>City</label>
                  <input style={input} value={form.city} onChange={setField('city')} autoComplete="address-level2" />
                </div>
                <div>
                  <label style={label}>State</label>
                  <input style={input} value={form.state} onChange={setField('state')} autoComplete="address-level1" />
                </div>
                <div>
                  <label style={label}>Zip</label>
                  <input style={input} value={form.zipCode} onChange={setField('zipCode')} autoComplete="postal-code" inputMode="numeric" />
                </div>
              </div>
              <label style={label}>Phone</label>
              <input style={input} value={form.phone} onChange={setField('phone')} autoComplete="tel" inputMode="tel" />
              <label style={label}>Email</label>
              <input style={input} value={form.email} onChange={setField('email')} autoComplete="email" inputMode="email" />

              {error && <p style={{ color: '#b91c1c', fontSize: 14, marginTop: 12, marginBottom: 0 }}>{error}</p>}

              <button style={{ ...btn, opacity: submitting ? 0.7 : 1 }} onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save My Information'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
